import React, { useMemo } from 'react'
import CanvasLogo from '../../../assets/icons/canvas-logo.svg'
import { LEVEL, LEVEL_ORDER, REQUIREMENT } from './utils/constants/levels'
import { STATUS } from './utils/constants/statuses'
import { isCompleteStatus, hasAdvancedPastLevel } from './utils/displayUtils'
import { ARTICLE_PATHS } from './utils/constants/articleUrls'
import { CANVAS_URLS } from './utils/constants/externalUrls'
import { LevelTrackerSection, RequirementsSection } from './ProgressTrackerContent'
import { useMembershipJourneyContext } from './MembershipJourneyContext'
import {
  Container,
  Header,
  TitleSection,
  Title,
  AssociateHeader,
  ProgramInfoItem,
  ProgramInfoLabel,
  ProgramInfoLink,
  PortalButton,
  PortalButtonText
} from './Styles/ProgressTracker.styles'

const ProgressTracker = ({
  activeLevel = LEVEL.QUALIFYING,
  onLevelClick
}) => {
  const { pgmProgress } = useMembershipJourneyContext()
  
  const {
    levels = [],
    currentLevelKey,
    mostRecentCompletedLevelKey,
    careerPath,
    patStatus,
    isSuspended = false
  } = pgmProgress || {}

  const trackerLevels = useMemo(() => {
    return levels.map(level => {
      return {
        key: level.key,
        name: level.name,
        completed: level.status === STATUS.COMPLETE,
        active: level.key === activeLevel,
        locked: level.status === STATUS.LOCKED,
        progress: hasAdvancedPastLevel(level.key, currentLevelKey, mostRecentCompletedLevelKey) ? 100 : level.progressPercent,
        status: level.status,
        hasIncompleteRequirements: level.key === LEVEL.QUALIFYING && patStatus && patStatus !== STATUS.COMPLETE
      }
    })
  }, [levels, activeLevel, patStatus, currentLevelKey, mostRecentCompletedLevelKey])

  const currentLevel = useMemo(() => 
    levels.find(l => l.key === activeLevel),
    [levels, activeLevel]
  )

  // Show a simplified level complete banner as a fallback when we can't display individual requirement statuses (ie., missing data from previous/completed levels)
  const shouldShowLevelCompleteBanner = useMemo(() => {
    if (!currentLevel) return false
    
    // Qualifying level is tracked differently - always show simplified banner
    if (currentLevel.key === LEVEL.QUALIFYING) {
      return true
    }
    
    const requirements = currentLevel.requirements || []
    const allRequirementsComplete = requirements.length > 0 && requirements.every(req => 
      isCompleteStatus(req.status)
    )
    
    if (hasAdvancedPastLevel(activeLevel, currentLevelKey, mostRecentCompletedLevelKey)) {
      if (requirements.length === 0 || !allRequirementsComplete) {
        return true
      }
    }
    
    return false
  }, [currentLevel, activeLevel, currentLevelKey, mostRecentCompletedLevelKey])

  const levelCompletionDate = currentLevel?.completionDate || null

  const patRequirementWithWarning = useMemo(() => {
    if (activeLevel !== LEVEL.QUALIFYING || !patStatus || patStatus === STATUS.COMPLETE) return null
    
    return {
      key: REQUIREMENT.PLAYING_ABILITY_TEST,
      name: 'Playing Ability Test (PAT)',
      status: patStatus
    }
  }, [activeLevel, patStatus])

  const shouldShowPATWarning = patStatus && patStatus !== STATUS.COMPLETE

  // Build additional requirements list (incl. PAT for non-qualifying levels)
  const userCurrentLevelIndex = LEVEL_ORDER.indexOf(currentLevelKey)
  const activeLevelIndex = LEVEL_ORDER.indexOf(activeLevel)
  const shouldShowAdditionalRequirements = activeLevelIndex >= userCurrentLevelIndex && !hasAdvancedPastLevel(activeLevel, currentLevelKey, mostRecentCompletedLevelKey)

  const additionalRequirements = useMemo(() => {
    if (activeLevel === LEVEL.QUALIFYING || !patStatus || patStatus === STATUS.COMPLETE || !shouldShowAdditionalRequirements) {
      return []
    }
    
    return [{
      key: REQUIREMENT.PLAYING_ABILITY_TEST,
      name: 'Playing Ability Test (PAT)',
      status: patStatus
    }]
  }, [activeLevel, patStatus, shouldShowAdditionalRequirements])

  return (
    <Container>
      <Header>
        <TitleSection>
          <Title>My Path to Membership</Title>
          {careerPath && (
            <ProgramInfoLink href={`https://www.pga.org${ARTICLE_PATHS.CAREER_PATHS}`} target="_blank" rel="noopener noreferrer">
              {careerPath} ›
            </ProgramInfoLink>
          )}
        </TitleSection>
        {currentLevelKey && currentLevelKey !== LEVEL.QUALIFYING && (
          <AssociateHeader>
            <ProgramInfoItem>
              <ProgramInfoLabel>Associate Program</ProgramInfoLabel>
              <ProgramInfoLink href="https://www.pga.org/membership/associate-program/" target="_blank" rel="noopener noreferrer">Details ›</ProgramInfoLink>
            </ProgramInfoItem>
          </AssociateHeader>
        )}
      </Header>

      <LevelTrackerSection 
        levels={trackerLevels}
        onLevelClick={onLevelClick}
        isSuspended={isSuspended}
        currentLevelKey={currentLevelKey}
        activeLevel={activeLevel}
      />

      <RequirementsSection
        shouldShowLevelCompleteBanner={shouldShowLevelCompleteBanner}
        currentLevel={currentLevel}
        levelCompletionDate={levelCompletionDate}
        patRequirementWithWarning={patRequirementWithWarning}
        additionalRequirements={additionalRequirements}
        activeLevel={activeLevel}
        shouldShowPATWarning={shouldShowPATWarning}
        shouldShowAdditionalRequirements={shouldShowAdditionalRequirements}
        onLevelClick={onLevelClick}
      />

      <PortalButton href={CANVAS_URLS.HOME} target="_blank" rel="noopener noreferrer">
        <img src={CanvasLogo} alt="Canvas Logo" />
        <PortalButtonText>Education Portal</PortalButtonText>
      </PortalButton>
    </Container>
  )
}

export default ProgressTracker
